import BodyText from '@ancon/wildcat-ui/web/orderweb/BodyText'
import useTranslation from 'next-translate/useTranslation'
import {
  OrderFormat,
  OutletDetails,
  OutletListItem,
} from '@ancon/wildcat-types'
import getOutletFormattedAddress from '@ancon/wildcat-utils/outlet/getOutletFormattedAddress'
import getOutletOrderFormats from '@ancon/wildcat-utils/outlet/getOutletOrderFormats'
import Button from '@ancon/wildcat-ui/web/orderweb/Button'
import OrderTypeIcon from '@ancon/wildcat-ui/web/orderweb/OrderTypeIcon'
import clsx from 'clsx'
import HeadingText from '@ancon/wildcat-ui/web/orderweb/HeadingText'
import formatPhoneNumber from '@ancon/wildcat-utils/phoneNumber/formatPhoneNumber'
import PhoneIcon from '@ancon/wildcat-ui/shared/icons/phone-volume-outline.svg'
import EmailIcon from '@ancon/wildcat-ui/shared/icons/email-outline.svg'
import LinkIcon from '@ancon/wildcat-ui/shared/icons/arrow-up-right-outline.svg'
import Link from 'next/link'

import useAppSelector from '../../../../store/hooks/useAppSelector'
import { outletsSelectedOutletListItemSelector } from '../../store/outletsSelectors'
import { orderFormatTextMap } from '../../constants'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import { outletSetIsVisibleOutletDetailsModal } from '../../../outlet/store/outletSlice'
import OutletCardOutletStatus from '../outletCard/OutletCardOutletStatus'
import isOutletAvailableForOrders from '../../../outlet/utils/isOutletAvailableForOrders'
import isOutletAcceptingOnlyPreOrdersWhenOpen from '../../../outlet/utils/isOutletAcceptingOnlyPreOrdersWhenOpen'

import styles from './NewOrderOutletDetails.module.scss'

type NewOrderOutletDetailsProps = {
  isInModal?: boolean
  /** Company variant: only the formats a company order can use */
  companyOrderFormats?: OrderFormat[]
  outletDetails: OutletDetails
  onClickOrderFormat: (orderFormat: OrderFormat) => void
}

function getAvailableOrderFormats(outlet: OutletListItem) {
  const orderFormats = getOutletOrderFormats(outlet)

  const isAcceptingOnlyPreOrdersWhenOpen =
    isOutletAcceptingOnlyPreOrdersWhenOpen(outlet)

  if (isAcceptingOnlyPreOrdersWhenOpen) {
    return orderFormats.filter(format => format !== OrderFormat.TableOrder)
  }

  return orderFormats
}

export default function NewOrderOutletDetails({
  isInModal,
  companyOrderFormats,
  outletDetails,
  onClickOrderFormat,
}: NewOrderOutletDetailsProps) {
  const { t } = useTranslation('outlets')
  const dispatch = useAppDispatch()

  const outletListItem = useAppSelector(outletsSelectedOutletListItemSelector)!

  const isAvailableForOrders = isOutletAvailableForOrders(outletListItem)

  const { name, address, telephone, email } = outletDetails

  const orderFormats =
    companyOrderFormats ?? getAvailableOrderFormats(outletListItem)
  const formattedAddress = getOutletFormattedAddress(address)

  function handleOnClickInfo() {
    dispatch(outletSetIsVisibleOutletDetailsModal(true))
  }

  const contactInfoRows = [
    {
      href: `mailto:${email}`,
      text: email,
      icon: <EmailIcon />,
    },
  ]

  if (telephone) {
    contactInfoRows.unshift({
      href: `tel:${telephone}`,
      text: formatPhoneNumber(telephone),
      icon: <PhoneIcon />,
    })
  }

  return (
    <div>
      <div className={styles.header}>
        <div
          className={clsx(styles.left, {
            [styles.grow]: !isInModal,
          })}
        >
          <HeadingText as="h2" className={styles.outletName}>
            {name}
          </HeadingText>
        </div>
        <div className={styles.right}>
          <Button
            variant="secondary"
            className={styles.infoButton}
            onClick={handleOnClickInfo}
          >
            {t('newOrderCard.info')}
          </Button>
        </div>
      </div>
      <BodyText color="body-1">{formattedAddress}</BodyText>
      <OutletCardOutletStatus
        inline
        outlet={outletListItem}
        containerClassName={styles.statusWrapper}
      />
      {orderFormats.length > 0 && (
        <div className={clsx(styles.row, styles.orderFormats)}>
          {orderFormats.map(orderFormat => (
            <Button
              key={orderFormat}
              onClick={() => onClickOrderFormat(orderFormat)}
              disabled={!isAvailableForOrders}
            >
              <OrderTypeIcon
                orderFormat={orderFormat}
                className={styles.orderFormatIcon}
              />
              <BodyText color="body-3" fontSize="1.6rem">
                {t(`common:${orderFormatTextMap[orderFormat as never]}`)}
              </BodyText>
            </Button>
          ))}
        </div>
      )}

      {orderFormats.length === 0 && (
        <BodyText className={styles.unavailableInfo} color="body-1">
          {t('newOrderCard.unavailable')}
        </BodyText>
      )}

      <div className={styles.contactInfoRows}>
        {contactInfoRows.map(info => (
          <Link key={info.href} href={info.href}>
            <span className={styles.contactInfoRow}>
              {info.icon}
              <BodyText fontSize="1.6rem">{info.text}</BodyText>
              <LinkIcon />
            </span>
          </Link>
        ))}
      </div>
    </div>
  )
}
